import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
import { supabase } from "../lib/supabase";
import { db } from "../lib/db";
import { storage } from "../lib/storage";
import { useApp } from "../context/AppContext";
import { notify } from "../utils/notify";
import { logAudit } from "../utils/auditLog";
import { useDebounce } from "../utils/debounce";
import Pagination from "./Pagination";

const PAGE_SIZE = 25;

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const dAgo = (days) => { const d = new Date(); d.setDate(d.getDate() - days); return d.toISOString(); };
const fmtTime = (iso) => { if (!iso) return "—"; return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); };
const fmtDate = (iso) => { if (!iso) return "—"; return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
const calcAge = (dob) => { if (!dob) return "?"; const b = new Date(dob), n = new Date(); return n.getFullYear() - b.getFullYear() - (n < new Date(n.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0); };
const formatDOB = (dob) => { if (!dob) return ""; const [y, m, d] = dob.split("-"); return `${m}/${d}/${y}`; };
const genConf = () => "FAX-" + Math.random().toString(36).slice(2, 8).toUpperCase();
const todayLong = () => new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

// Maps a real fax_transmissions row (inbound) to the field names the UI expects.
const normalizeInbound = (tx) => ({
  ...tx,
  subject:          `Inbound Fax · ${tx.from_number || 'Unknown'}`,
  sender_name:      tx.from_number || '—',
  sender_fax:       tx.from_number || '—',
  received_at:      tx.sent_at,
  unread:           !tx.routed_at,
  patient_assigned: !!tx.patient_id,
  routed_to:        tx.routed_by || null,
});

// ─── MOCK DATA ─────────────────────────────────────────────────────────────────
export const MOCK_FAX_CONTACTS = [
  { id: "FC001", name: "CVS Pharmacy Scottsdale",        organization: "CVS Health",                  fax_number: "(480) 555-0201", phone: "(480) 555-0200", category: "Pharmacy",      notes: "Store #4421" },
  { id: "FC002", name: "Walgreens Paradise Valley",      organization: "Walgreens",                   fax_number: "(480) 555-0202", phone: "(480) 555-0203", category: "Pharmacy",      notes: "" },
  { id: "FC003", name: "Blue Cross Blue Shield AZ",      organization: "BCBS Arizona",                fax_number: "(602) 555-0301", phone: "(602) 555-0300", category: "Insurance",     notes: "Prior auth fax line" },
  { id: "FC004", name: "Aetna Prior Auth",               organization: "Aetna",                       fax_number: "(602) 555-0302", phone: "(602) 555-0303", category: "Insurance",     notes: "Auth requests" },
  { id: "FC005", name: "UnitedHealthcare",               organization: "UHC",                         fax_number: "(602) 555-0303", phone: "(602) 555-0304", category: "Insurance",     notes: "" },
  { id: "FC006", name: "Banner Estrella Medical Center", organization: "Banner Health",               fax_number: "(623) 555-0401", phone: "(623) 555-0400", category: "Hospital/ASC",  notes: "Records dept" },
  { id: "FC007", name: "Mayo Clinic Scottsdale",         organization: "Mayo Clinic",                 fax_number: "(480) 555-0402", phone: "(480) 555-0403", category: "Hospital/ASC",  notes: "" },
  { id: "FC008", name: "Arizona Hand Center",            organization: "Arizona Hand Center",         fax_number: "(480) 555-0501", phone: "(480) 555-0500", category: "Specialist",    notes: "Referrals" },
  { id: "FC009", name: "SimonMed Imaging",               organization: "SimonMed",                    fax_number: "(480) 555-0601", phone: "(480) 555-0600", category: "Imaging",       notes: "Orders and results" },
  { id: "FC010", name: "Dr. Sarah Kim, MD",              organization: "Desert Ridge Family Medicine",fax_number: "(480) 555-0701", phone: "(480) 555-0700", category: "Primary Care",  notes: "Referring PCP" },
  { id: "FC011", name: "Johnson & Associates",           organization: "Johnson & Associates Law",    fax_number: "(602) 555-0801", phone: "(602) 555-0800", category: "Attorney",      notes: "PI cases" },
  { id: "FC012", name: "StateFund WC Adjusters",         organization: "Arizona State Fund",          fax_number: "(602) 555-0901", phone: "(602) 555-0900", category: "Workers Comp",  notes: "WC claims" },
];

export const MOCK_FAX_INBOX = [
  {
    id: "FX001", direction: "inbound", status: "received", unread: true,
    sender_name: "Blue Cross Blue Shield AZ", sender_fax: "(602) 555-0301",
    subject: "Authorization Approval — Shoulder MRI", pages: 2,
    received_at: dAgo(0.02), patient_id: "P001", patient_assigned: true, routed_to: null,
    body: `AUTHORIZATION APPROVAL\n\nDate: ${fmtDate(new Date().toISOString())}\nMember: James Hartley\nMember ID: BCB-881-44291\n\nService: MRI Right Shoulder without contrast\nAuthorization #: AUTH-2026-04412\nApproved Units: 1\nValid Through: 2026-06-22\n\nThis authorization does not guarantee payment. Benefits are subject to eligibility at time of service.\n\nBlue Cross Blue Shield of Arizona\nPrior Authorization Department\n(602) 555-0300`,
  },
  {
    id: "FX002", direction: "inbound", status: "received", unread: true,
    sender_name: "Dr. Sarah Kim, MD", sender_fax: "(480) 555-0701",
    subject: "Referral — Carpal Tunnel Evaluation", pages: 3,
    received_at: dAgo(1), patient_id: "P002", patient_assigned: true, routed_to: "Dr. Walker",
    body: `REFERRAL\n\nDate: ${fmtDate(dAgo(1))}\nFrom: Sarah Kim, MD\nDesert Ridge Family Medicine\n(480) 555-0700\n\nTo: Robert Walker, MD\nDesert Orthopedics Center\n\nRE: Maria Delgado, DOB 09/22/1982\n\nDear Dr. Walker,\n\nI am referring Ms. Delgado for evaluation and treatment of right carpal tunnel syndrome. She has been experiencing progressive numbness and tingling in her right hand for approximately 9 months with worsening nocturnal symptoms. EMG/NCS performed 2/15/2026 confirmed moderate CTS.\n\nPMH: HTN, T2DM\nMedications: Metformin, Lisinopril, wrist splint\n\nPlease advise regarding surgical intervention.\n\nSarah Kim, MD\n(480) 555-0700`,
  },
  {
    id: "FX003", direction: "inbound", status: "received", unread: false,
    sender_name: "Banner Estrella Medical Center", sender_fax: "(623) 555-0401",
    subject: "Outside Operative Report — Robert Chen", pages: 8,
    received_at: dAgo(2), patient_id: null, patient_assigned: false, routed_to: null,
    body: `OPERATIVE REPORT\n\nPatient: Robert Chen, DOB 12/08/1975\nDate of Surgery: 01/15/2026\nSurgeon: James Park, MD\n\nPROCEDURE: Right trigger finger release, ring finger\n\nINDICATION: Patient presented with locking of right ring finger, grade III trigger finger. Conservative management failed.\n\nTECHNIQUE: Patient positioned supine. Digital block performed. Transverse incision at A1 pulley, complete release performed. Wound closed with 4-0 nylon. No complications.\n\nESTIMATED BLOOD LOSS: Minimal\nDISPOSITION: Home\n\nJames Park, MD\nBanner Estrella Medical Center`,
  },
  {
    id: "FX004", direction: "inbound", status: "received", unread: true,
    sender_name: "CVS Pharmacy Scottsdale", sender_fax: "(480) 555-0201",
    subject: "Callback — Controlled Substance Refill Request", pages: 1,
    received_at: dAgo(0.05), patient_id: "P003", patient_assigned: true, routed_to: null,
    body: `CVS PHARMACY — FAX CALLBACK\n\nDate: ${fmtDate(new Date().toISOString())}\nStore: Scottsdale #4421\nPhone: (480) 555-0200\n\nPatient: Robert Chen\nDOB: 12/08/1975\nMedication: Oxycodone 5mg #30\nPrescriber: Robert Walker, MD\n\nPatient is requesting early refill. Current prescription was filled 02/28/2026 (23 days ago). Per plan guidelines, this is too early for refill by 7 days.\n\nPlease call (480) 555-0200 to authorize or deny.\n\nThank you,\nCVS Pharmacy Team`,
  },
  {
    id: "FX005", direction: "inbound", status: "received", unread: false,
    sender_name: "StateFund WC Adjusters", sender_fax: "(602) 555-0901",
    subject: "Workers Comp — Claim Update DK-2026-00418", pages: 4,
    received_at: dAgo(3), patient_id: "P005", patient_assigned: true, routed_to: "Dr. Walker",
    body: `ARIZONA STATE COMPENSATION FUND\nCLAIM UPDATE\n\nClaim #: DK-2026-00418\nClaimant: David Okafor\nDOB: 02/17/1990\nDate of Injury: 02/10/2026\nEmployer: Phoenix Metro Construction\n\nAdjuster: Marcus Webb\n(602) 555-0900\n\nDear Dr. Walker,\n\nWe have reviewed the medical documentation for the above-referenced claim. Authorization is granted for:\n- Initial evaluation and management\n- Right thumb UCL repair\n- Post-operative physical therapy (up to 12 visits)\n\nAll future treatment requests must be submitted on form ICA-1 with clinical justification. Please send updated work restrictions.\n\nMarcus Webb\nClaims Adjuster, AZ State Fund`,
  },
];

const MOCK_SENT = [
  { id: "FS001", direction: "outbound", status: "confirmed", recipient_name: "Blue Cross Blue Shield AZ", recipient_fax: "(602) 555-0301", patient_id: "P001", patient_name: "James Hartley",    subject: "Prior Auth Request — Shoulder MRI",       pages: 3, sent_at: dAgo(5),  confirmed_at: dAgo(4.9), confirmation_number: "FAX-A9KX4M", failed_reason: null, is_referral: false, fax_body: "Prior authorization request for MRI right shoulder without contrast. Dx: M75.121 Rotator cuff tear, right." },
  { id: "FS002", direction: "outbound", status: "sent",      recipient_name: "Arizona Hand Center",         recipient_fax: "(480) 555-0501", patient_id: "P002", patient_name: "Maria Delgado",    subject: "Referral — Carpal Tunnel Syndrome",        pages: 4, sent_at: dAgo(3),  confirmed_at: null,       confirmation_number: "FAX-BT7R2Q", failed_reason: null, is_referral: true,  fax_body: "Referral letter for carpal tunnel evaluation and management." },
  { id: "FS003", direction: "outbound", status: "failed",    recipient_name: "SimonMed Imaging",             recipient_fax: "(480) 555-0601", patient_id: "P003", patient_name: "Robert Chen",      subject: "Imaging Order — Right Hand X-Ray Series",  pages: 2, sent_at: dAgo(2),  confirmed_at: null,       confirmation_number: null,          failed_reason: "Line busy — maximum retries reached", is_referral: false, fax_body: "Imaging order: Right hand X-ray AP/Lateral/Oblique." },
  { id: "FS004", direction: "outbound", status: "confirmed", recipient_name: "Aetna Prior Auth",             recipient_fax: "(602) 555-0302", patient_id: "P004", patient_name: "Patricia Monroe",  subject: "Prior Auth — CMC Arthroplasty",             pages: 5, sent_at: dAgo(7),  confirmed_at: dAgo(6.8), confirmation_number: "FAX-CW3N8P", failed_reason: null, is_referral: false, fax_body: "Prior authorization for thumb CMC arthroplasty. Conservative treatment failed." },
  { id: "FS005", direction: "outbound", status: "queued",    recipient_name: "UnitedHealthcare",             recipient_fax: "(602) 555-0303", patient_id: "P005", patient_name: "David Okafor",     subject: "Work Status Report — UCL Repair",           pages: 2, sent_at: null,     confirmed_at: null,       confirmation_number: null,          failed_reason: null, is_referral: false, fax_body: "Post-operative work status report following UCL repair right thumb." },
];

const MOCK_DRAFTS = [
  { id: "FD001", status: "draft", recipient_name: "Mayo Clinic Scottsdale", recipient_fax: "(480) 555-0402", patient_id: "P001", patient_name: "James Hartley",   subject: "Complex Shoulder — Second Opinion Request", fax_body: "Dear Colleague,\n\nI am writing to request a second opinion regarding the management of Mr. James Hartley, a 57-year-old male with a complete rotator cuff tear...", created_at: dAgo(1),   pages: 3 },
  { id: "FD002", status: "draft", recipient_name: "",                        recipient_fax: "",               patient_id: "P004", patient_name: "Patricia Monroe", subject: "Medicare Auth — Bone Density Study",         fax_body: "Bone density study request for Patricia Monroe, Medicare beneficiary...",                                                                                                              created_at: dAgo(0.5), pages: 2 },
];

const AUDIT_INBOX = [
  { timestamp: dAgo(0.02),  action: "received",  user: "System",      patient: "James Hartley",  counterparty: "BCBS AZ" },
  { timestamp: dAgo(0.05),  action: "received",  user: "System",      patient: "Robert Chen",    counterparty: "CVS Pharmacy" },
  { timestamp: dAgo(1),     action: "received",  user: "System",      patient: "Maria Delgado",  counterparty: "Dr. Sarah Kim" },
  { timestamp: dAgo(1.1),   action: "assigned",  user: "Dr. Walker",  patient: "Maria Delgado",  counterparty: "Dr. Sarah Kim" },
  { timestamp: dAgo(1.15),  action: "routed",    user: "Dr. Walker",  patient: "Maria Delgado",  counterparty: "Dr. Walker" },
  { timestamp: dAgo(2),     action: "received",  user: "System",      patient: "Unassigned",     counterparty: "Banner Estrella" },
  { timestamp: dAgo(3),     action: "received",  user: "System",      patient: "David Okafor",   counterparty: "StateFund WC" },
];
const AUDIT_SENT = [
  { timestamp: dAgo(5),    action: "sent",       user: "Dr. Walker",  patient: "James Hartley",   counterparty: "BCBS AZ" },
  { timestamp: dAgo(4.9),  action: "confirmed",  user: "System",      patient: "James Hartley",   counterparty: "BCBS AZ" },
  { timestamp: dAgo(7),    action: "sent",       user: "Dr. Walker",  patient: "Patricia Monroe", counterparty: "Aetna Prior Auth" },
  { timestamp: dAgo(6.8),  action: "confirmed",  user: "System",      patient: "Patricia Monroe", counterparty: "Aetna Prior Auth" },
  { timestamp: dAgo(3),    action: "sent",       user: "Dr. Walker",  patient: "Maria Delgado",   counterparty: "Arizona Hand Center" },
  { timestamp: dAgo(2),    action: "sent",       user: "Front Desk",  patient: "Robert Chen",     counterparty: "SimonMed Imaging" },
  { timestamp: dAgo(2),    action: "failed",     user: "System",      patient: "Robert Chen",     counterparty: "SimonMed Imaging" },
];

const CONTACT_CATEGORIES = ["Pharmacy","Insurance","Primary Care","Specialist","Hospital/ASC","Attorney","Workers Comp","Imaging","Other"];
const CAT_ICONS = { Pharmacy:"💊", Insurance:"🛡", "Primary Care":"🏥", Specialist:"👨‍⚕️", "Hospital/ASC":"🏨", Attorney:"⚖", "Workers Comp":"🦺", Imaging:"🔬", Other:"📋" };

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = { queued:"badge-gray", sent:"badge-teal", confirmed:"badge-green", failed:"badge-red", draft:"badge-amber", received:"badge-teal" };
  const labels = { queued:"Queued", sent:"Sent", confirmed:"Confirmed", failed:"Failed", draft:"Draft", received:"Received" };
  return <span className={`badge ${map[status] || "badge-gray"}`}>{labels[status] || status}</span>;
}

// ─── AUDIT LOG ─────────────────────────────────────────────────────────────────
function AuditLog({ entries }) {
  const exportCSV = () => {
    const rows = [["Timestamp","Action","User","Patient","Recipient/Sender"], ...entries.map(e => [fmtTime(e.timestamp), e.action, e.user, e.patient, e.counterparty])];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = "data:text/csv," + encodeURIComponent(csv); a.download = "fax_audit.csv"; a.click();
  };
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>Audit Log</div>
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={exportCSV}>Export CSV</button>
      </div>
      <div style={{ background: "var(--navy-3)", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: "var(--navy-4)" }}>
              {["Timestamp","Action","User","Patient","Recipient/Sender"].map(h => (
                <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: "var(--text-3)", fontWeight: 500, borderBottom: "1px solid var(--border)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={i} style={{ borderBottom: i < entries.length - 1 ? "1px solid var(--border)" : "none" }}>
                <td style={{ padding: "5px 10px", fontFamily: "var(--mono)", color: "var(--text-3)" }}>{fmtTime(e.timestamp)}</td>
                <td style={{ padding: "5px 10px" }}><span className={`badge ${e.action==="sent"||e.action==="confirmed"?"badge-teal":e.action==="received"?"badge-green":e.action==="failed"?"badge-red":"badge-gray"}`}>{e.action}</span></td>
                <td style={{ padding: "5px 10px", color: "var(--text-2)" }}>{e.user}</td>
                <td style={{ padding: "5px 10px", color: "var(--text-2)" }}>{e.patient}</td>
                <td style={{ padding: "5px 10px", color: "var(--text-3)" }}>{e.counterparty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── FAX VIEWER ───────────────────────────────────────────────────────────────
function FaxViewer({ fax, onAssign, onRoute, onReply, patients, onViewPDF, onLinkPatient }) {
  const [assignPt, setAssignPt] = useState("");
  const [routeTo, setRouteTo] = useState("");

  if (!fax) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)" }}>
      <div style={{ textAlign: "center" }}><div style={{ fontSize: 36, marginBottom: 8 }}>📠</div><div>Select a fax to preview</div></div>
    </div>
  );

  // Only treat as viewable if we have a permanent storage path (not a stale S3 URL)
  const hasPDF = !!(fax.storage_path && !fax.storage_path.includes('undefined'));

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "var(--navy-2)", borderLeft: "1px solid var(--border)", overflow: "hidden" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--navy-3)", flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)", marginBottom: 4 }}>{fax.subject}</div>
        <div style={{ fontSize: 12, color: "var(--text-3)", display: "flex", gap: 16, flexWrap: "wrap" }}>
          <span>From: <strong style={{ color: "var(--text-2)" }}>{fax.sender_name} · {fax.sender_fax}</strong></span>
          <span>Received: <strong style={{ color: "var(--text-2)" }}>{fmtTime(fax.received_at)}</strong></span>
          <span>Pages: <strong style={{ color: "var(--text-2)" }}>{fax.pages}</strong></span>
          {fax.routed_to && <span>Routed to: <strong style={{ color: "var(--teal)" }}>{fax.routed_to}</strong></span>}
        </div>
      </div>
      <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
        {hasPDF && (
          <button className="btn btn-primary btn-sm" onClick={() => onViewPDF && onViewPDF(fax)}>📄 View PDF</button>
        )}
        {fax.patient_assigned
          ? <span className="badge badge-green">✓ Patient Linked</span>
          : onLinkPatient
            ? <button className="btn btn-secondary btn-sm" onClick={() => onLinkPatient(fax)}>🔗 Link to Patient</button>
            : (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <select value={assignPt} onChange={e => setAssignPt(e.target.value)} className="input" style={{ padding: "4px 8px", fontSize: 12, width: 170 }}>
                  <option value="">Assign to patient...</option>
                  {(patients || []).map(p => <option key={p.id} value={p.id}>{p.first} {p.last}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" disabled={!assignPt} onClick={() => { onAssign && onAssign(fax.id, assignPt); setAssignPt(""); }}>Assign</button>
              </div>
            )
        }
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <select value={routeTo} onChange={e => setRouteTo(e.target.value)} className="input" style={{ padding: "4px 8px", fontSize: 12, width: 170 }}>
            <option value="">Route to provider...</option>
            {["Dr. Walker","Dr. Martinez","Nurse Chen","Front Desk"].map(p => <option key={p}>{p}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" disabled={!routeTo} onClick={() => { onRoute && onRoute(fax.id, routeTo); setRouteTo(""); }}>Route</button>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => onReply && onReply(fax)}>Reply by Fax</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        {hasPDF ? (
          <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-2)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>PDF Fax Attachment</div>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 20 }}>{fax.pages || '?'} page{fax.pages !== 1 ? 's' : ''}</div>
            <button className="btn btn-primary" onClick={() => onViewPDF && onViewPDF(fax)}>Open PDF →</button>
          </div>
        ) : fax.direction === 'inbound' ? (
          <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-3)" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏱️</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-2)", marginBottom: 8 }}>PDF Expired</div>
            <div style={{ fontSize: 13, maxWidth: 360, margin: "0 auto", lineHeight: 1.6 }}>
              This fax was received more than 1 hour ago and the temporary link has expired.
              Future faxes are permanently stored and will always be viewable.
            </div>
          </div>
        ) : (
          <div style={{ background: "var(--navy-4)", color: "var(--text-1)", border: "1px solid var(--border)", borderRadius: 8, padding: "24px 28px", fontFamily: "Georgia, serif", fontSize: 13, lineHeight: 1.85, whiteSpace: "pre-wrap", maxWidth: 640 }}>
            {fax.body || fax.fax_body || "(No fax body)"}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LINK TO PATIENT MODAL ────────────────────────────────────────────────────
function LinkToPatientModal({ fax, patients, currentProvider, onClose, onLinked }) {
  const [search, setSrch] = useState('');
  const [notes,  setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const filtered = (patients || []).filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = `${p.first_name || p.first || ''} ${p.last_name || p.last || ''}`.toLowerCase();
    return name.includes(q) || (p.mrn || '').toLowerCase().includes(q);
  });

  async function handleLink(patient) {
    if (!patient?.id || !/^[0-9a-f-]{36}$/.test(patient.id)) {
      console.error('[fax-link] invalid patient ID:', patient?.id);
      notify('Invalid patient ID — please select a valid patient', 'error');
      return;
    }
    setSaving(true);
    const { error } = await db.from('fax_transmissions').update({
      patient_id:    patient.id,
      routed_by:     currentProvider?.email || 'staff',
      routed_at:     new Date().toISOString(),
      routing_notes: notes || null,
    }).eq('id', fax.id);

    if (error) {
      console.error('[fax-link] routing error:', error.message);
      notify('Failed to link patient: ' + error.message, 'error');
      setSaving(false);
    } else {
      onLinked(fax.id, patient);
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'var(--navy-2)', border: '1px solid var(--border-2)', borderRadius: 12, padding: 24, width: 'min(440px,94vw)', boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>🔗 Link Fax to Patient</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16 }}>
          From: {fax.sender_name} · {fax.pages || '?'}pp · {fmtTime(fax.received_at)}
        </div>
        <input
          className="input"
          placeholder="Search by name or MRN..."
          value={search}
          onChange={e => setSrch(e.target.value)}
          style={{ marginBottom: 10 }}
          autoFocus
        />
        <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 10, border: '1px solid var(--border)', borderRadius: 7 }}>
          {filtered.slice(0, 20).map(p => {
            const name = `${p.first_name || p.first || ''} ${p.last_name || p.last || ''}`.trim();
            return (
              <div key={p.id}
                onClick={() => !saving && handleLink(p)}
                className="tr-clickable"
                style={{ padding: '9px 14px', borderBottom: '1px solid var(--border)', cursor: saving ? 'wait' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{name || '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>MRN: {p.mrn || '—'}</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--teal)' }}>{saving ? '…' : 'Link →'}</span>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No patients found</div>
          )}
        </div>
        <textarea
          className="input"
          placeholder="Routing notes (optional)..."
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          style={{ marginBottom: 12, resize: 'vertical' }}
        />
        <button className="btn btn-ghost" onClick={onClose} style={{ width: '100%' }}>Cancel</button>
      </div>
    </div>,
    document.body
  );
}

// ─── CONTACT SEARCH DROPDOWN ──────────────────────────────────────────────────
function ContactSearch({ contacts, value, onChange, onSelect }) {
  const filtered = contacts.filter(c => value && (c.name.toLowerCase().includes(value.toLowerCase()) || c.organization.toLowerCase().includes(value.toLowerCase())));
  return (
    <div style={{ position: "relative" }}>
      <input className="input" placeholder="Search fax contacts..." value={value} onChange={e => onChange(e.target.value)} style={{ fontSize: 12 }} />
      {value && filtered.length > 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--navy-3)", border: "1px solid var(--border-2)", borderRadius: 6, zIndex: 50, maxHeight: 180, overflowY: "auto", boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
          {filtered.map(c => (
            <div key={c.id} onClick={() => { onSelect(c); onChange(""); }} style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid var(--border)", fontSize: 12 }} className="tr-clickable">
              <div style={{ fontWeight: 500 }}>{c.name}</div>
              <div style={{ color: "var(--text-3)", fontSize: 11 }}>{c.fax_number} · {c.category}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SEND FAX PANEL ───────────────────────────────────────────────────────────
export function FaxPanel({ patient, onClose, prefill = null, patients }) {
  const [contacts] = useState(isDemoMode ? MOCK_FAX_CONTACTS : []);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [manualName, setManualName] = useState(prefill?.recipient_name || "");
  const [manualFax, setManualFax] = useState(prefill?.recipient_fax || "");
  const [saveContact, setSaveContact] = useState(false);
  const [subject, setSubject] = useState(prefill?.subject || "");
  const [faxBody, setFaxBody] = useState(prefill?.fax_body || "");
  const [priority, setPriority] = useState("routine");
  const [coverSheet, setCoverSheet] = useState(true);
  const [coverMsg, setCoverMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // null | { type: "sent"|"draft", conf?: string }

  const recipientName = selectedContact ? selectedContact.name : manualName;
  const recipientFax  = selectedContact ? selectedContact.fax_number : manualFax;
  const patientName   = patient ? `${patient.first} ${patient.last}` : "";
  const patientDOB    = patient ? formatDOB(patient.dob) : "";
  const estPages      = Math.max(1, Math.ceil((faxBody.length || 1) / 1800)) + (coverSheet ? 1 : 0);

  const handleSend = async (draft = false) => {
    setSending(true);
    const rec = {
      patient_id: patient?.id || null, direction: "outbound",
      status: draft ? "draft" : "queued",
      recipient_name: recipientName, recipient_fax: recipientFax,
      sender_name: "Desert Orthopedics Center — Dr. R. Walker", sender_fax: "(480) 555-0100",
      subject, cover_sheet_message: coverSheet ? coverMsg : null,
      pages: estPages, fax_body: faxBody, is_referral: !!prefill?.is_referral, notes: "",
    };
    try {
      const { data } = await db.from("faxes").insert([rec]).select().single();
      if (!draft && data) {
        logAudit('fax', 'document', data.id, rec.patient_id || null, { recipient: rec.recipient_name, subject: rec.subject });
        setTimeout(async () => {
          const conf = genConf();
          await db.from("faxes").update({ status: "sent", sent_at: new Date().toISOString(), confirmation_number: conf }).eq("id", data.id);
          setResult({ type: "sent", conf }); setSending(false);
        }, 2000);
        return;
      }
    } catch { /* fall through to mock */ }
    if (!draft) { setTimeout(() => { setResult({ type: "sent", conf: genConf() }); setSending(false); }, 2000); }
    else { setResult({ type: "draft" }); setSending(false); }
  };

  if (result) return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1010 }}>
      <div style={{ background: "var(--navy-2)", border: "1px solid var(--border)", borderRadius: 12, padding: "32px 40px", width: 380, textAlign: "center" }}>
        <div style={{ fontSize: 42, marginBottom: 14 }}>{result.type === "draft" ? "📝" : "✅"}</div>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{result.type === "draft" ? "Draft Saved" : "Fax Sent"}</div>
        <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: result.conf ? 6 : 24 }}>{result.type === "draft" ? "Your draft is saved. Resume it from the Fax page." : "Your fax is transmitting to the recipient."}</div>
        {result.conf && <div style={{ fontSize: 12, fontFamily: "var(--mono)", color: "var(--text-3)", marginBottom: 24 }}>Confirmation: {result.conf}</div>}
        <button className="btn btn-primary" onClick={onClose}>Done</button>
      </div>
    </div>,
    document.body
  );

  return createPortal(
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 520, background: "var(--navy-2)", borderLeft: "1px solid var(--border)", zIndex: 960, display: "flex", flexDirection: "column", boxShadow: "-4px 0 28px rgba(0,0,0,0.45)" }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 22 }}>📠</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Send Fax</div>
          {patient && <div style={{ fontSize: 12, color: "var(--text-3)" }}>{patient.first} {patient.last} · {patient.mrn}</div>}
        </div>
        <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

        {/* Recipient */}
        <div style={{ marginBottom: 16 }}>
          <div className="form-label">Recipient</div>
          <ContactSearch contacts={contacts} value={contactSearch} onChange={setContactSearch} onSelect={(c) => { setSelectedContact(c); setManualName(""); setManualFax(""); }} />
          {selectedContact && (
            <div style={{ marginTop: 8, background: "var(--teal-lt)", border: "1px solid var(--teal-md)", borderRadius: 6, padding: "8px 12px", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 600, color: "var(--teal)" }}>{selectedContact.name}</div>
                <div style={{ color: "var(--text-2)", fontSize: 11 }}>{selectedContact.fax_number} · {selectedContact.category}</div>
              </div>
              <button onClick={() => setSelectedContact(null)} style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>✕</button>
            </div>
          )}
          {!selectedContact && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <input placeholder="Recipient name" className="input" style={{ fontSize: 12 }} value={manualName} onChange={e => setManualName(e.target.value)} />
              <input placeholder="Fax number" className="input" style={{ fontSize: 12 }} value={manualFax} onChange={e => setManualFax(e.target.value)} />
            </div>
          )}
          {!selectedContact && (manualName || manualFax) && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 12, color: "var(--text-2)", cursor: "pointer" }}>
              <input type="checkbox" checked={saveContact} onChange={e => setSaveContact(e.target.checked)} />
              Save as contact
            </label>
          )}
        </div>

        {/* Subject */}
        <div style={{ marginBottom: 16 }}>
          <div className="form-label">Subject</div>
          <input className="input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Fax subject line..." />
        </div>

        {/* Priority */}
        <div style={{ marginBottom: 16 }}>
          <div className="form-label">Priority</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setPriority("routine")} className={`btn btn-sm ${priority === "routine" ? "btn-secondary" : "btn-ghost"}`}>Routine</button>
            <button onClick={() => setPriority("urgent")} className={`btn btn-sm ${priority === "urgent" ? "btn-primary" : "btn-ghost"}`} style={priority === "urgent" ? { background: "var(--red)", borderColor: "var(--red)" } : {}}>⚡ Urgent</button>
          </div>
        </div>

        {/* Cover Sheet */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 8 }}>
            <input type="checkbox" checked={coverSheet} onChange={e => setCoverSheet(e.target.checked)} />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Include Cover Sheet</span>
          </label>
          {coverSheet && (
            <div style={{ background: "var(--navy-3)", border: "1px solid var(--border)", borderRadius: 8, padding: "14px 16px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "72px 1fr", gap: "4px 8px", fontSize: 12, marginBottom: 12 }}>
                <span style={{ color: "var(--text-3)", fontWeight: 500 }}>To:</span>      <span style={{ color: "var(--text-1)" }}>{recipientName || "(recipient)"}{recipientFax ? ` — ${recipientFax}` : ""}</span>
                <span style={{ color: "var(--text-3)", fontWeight: 500 }}>From:</span>    <span style={{ color: "var(--text-1)" }}>Desert Orthopedics Center — Dr. R. Walker</span>
                <span style={{ color: "var(--text-3)", fontWeight: 500 }}>Date:</span>    <span style={{ color: "var(--text-1)" }}>{todayLong()}</span>
                <span style={{ color: "var(--text-3)", fontWeight: 500 }}>Re:</span>      <span style={{ color: "var(--text-1)" }}>{patientName || "(patient name)"}{patientDOB ? `, DOB ${patientDOB}` : ""}</span>
                <span style={{ color: "var(--text-3)", fontWeight: 500 }}>Pages:</span>   <span style={{ color: "var(--text-1)" }}>{estPages} (including cover)</span>
              </div>
              <div className="form-label" style={{ marginBottom: 4 }}>Cover Sheet Message</div>
              <textarea className="input" rows={3} value={coverMsg} onChange={e => setCoverMsg(e.target.value)} placeholder="Optional message for cover page..." style={{ fontSize: 12, resize: "vertical" }} />
              <div style={{ marginTop: 10, padding: "8px 10px", background: "var(--navy-4)", borderRadius: 4, fontSize: 11, color: "var(--text-3)", lineHeight: 1.65 }}>
                <strong style={{ color: "var(--amber)" }}>CONFIDENTIALITY NOTICE:</strong> This facsimile contains confidential health information intended solely for the named recipient. If received in error please destroy immediately and notify sender at (480) 555-0100.
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ marginBottom: 8 }}>
          <div className="form-label">Fax Body</div>
          <textarea className="input" rows={10} value={faxBody} onChange={e => setFaxBody(e.target.value)} placeholder="Type fax content here, or paste a generated letter..." style={{ resize: "vertical", fontFamily: "var(--mono)", fontSize: 12, lineHeight: 1.6 }} />
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>Estimated {estPages} page{estPages !== 1 ? "s" : ""} total</div>
        </div>
      </div>

      <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, flexShrink: 0 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => handleSend(true)} disabled={sending}>Save Draft</button>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => handleSend(false)} disabled={sending || (!recipientName && !recipientFax)}>
          {sending ? "Sending…" : "📠 Send Fax"}
        </button>
      </div>
    </div>,
    document.body
  );
}

// ─── REFERRAL PANEL ───────────────────────────────────────────────────────────
export function FaxReferralPanel({ patient, onClose }) {
  const [contactSearch, setContactSearch] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientFax, setRecipientFax] = useState("");
  const [showSend, setShowSend] = useState(false);

  const age    = patient ? calcAge(patient.dob) : "";
  const sex    = patient?.sex === "M" ? "male" : patient?.sex === "F" ? "female" : "patient";
  const dob    = patient ? formatDOB(patient.dob) : "";
  const probs  = patient?.problems || [];
  const active = probs.filter(p => p.status === "active");
  const dx     = active.length ? active.map(p => p.name).join(", ") : "orthopedic condition";
  const chiefDx = active.length ? active[0].name.toLowerCase() : dx.toLowerCase();

  const defaultLetter =
`${todayLong()}

To: ${recipientName || "[Recipient]"}
From: Robert Walker, MD — Upper Extremity Surgery

RE: ${patient ? `${patient.first} ${patient.last}, DOB ${dob}` : "[Patient]"}

Dear Colleague,

I am referring the above-named patient for evaluation and management of ${dx}.

${patient?.first || "The patient"} is a ${age}-year-old ${sex} presenting with ${chiefDx}.

Relevant history:
${probs.length > 0 ? probs.map(p => `  • ${p.name} (${p.code}) — ${p.status}`).join("\n") : "  • No active problems documented"}

Current medications: Please see attached medication list.

Relevant imaging: Please see attached imaging studies.

Please see attached records. I appreciate your assistance in the care of this patient.

Sincerely,
Robert Walker, MD
Upper Extremity Surgery
Desert Orthopedics Center
(480) 555-0100`;

  const [letter, setLetter] = useState(defaultLetter);

  if (showSend) return (
    <FaxPanel
      patient={patient}
      onClose={onClose}
      prefill={{ recipient_name: recipientName, recipient_fax: recipientFax, subject: `Referral — ${patient ? `${patient.first} ${patient.last}` : "Patient"}`, fax_body: letter, is_referral: true }}
    />
  );

  return createPortal(
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 600, background: "var(--navy-2)", borderLeft: "1px solid var(--border)", zIndex: 960, display: "flex", flexDirection: "column", boxShadow: "-4px 0 28px rgba(0,0,0,0.45)" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 22 }}>📋</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Fax Referral</div>
          {patient && <div style={{ fontSize: 12, color: "var(--text-3)" }}>{patient.first} {patient.last} · {patient.mrn}</div>}
        </div>
        <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        {/* Recipient */}
        <div style={{ marginBottom: 16 }}>
          <div className="form-label">Send To</div>
          <ContactSearch contacts={isDemoMode ? MOCK_FAX_CONTACTS : []} value={contactSearch} onChange={setContactSearch} onSelect={c => { setRecipientName(c.name); setRecipientFax(c.fax_number); setLetter(prev => prev.replace("[Recipient]", c.name)); setContactSearch(""); }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
            <input placeholder="Recipient name" className="input" style={{ fontSize: 12 }} value={recipientName} onChange={e => setRecipientName(e.target.value)} />
            <input placeholder="Fax number" className="input" style={{ fontSize: 12 }} value={recipientFax} onChange={e => setRecipientFax(e.target.value)} />
          </div>
        </div>

        {/* Letterhead + editable letter */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div className="form-label" style={{ margin: 0 }}>Referral Letter</div>
            <span style={{ fontSize: 11, color: "var(--text-3)" }}>Auto-populated — edit as needed</span>
          </div>
          <div style={{ background: "var(--navy-4)", border: "1px solid var(--border)", padding: "12px 16px", borderRadius: "8px 8px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--teal)" }}>Desert Orthopedics Center</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>Robert Walker, MD · Upper Extremity Surgery · (480) 555-0100</div>
            </div>
            <div style={{ fontSize: 11, color: "var(--text-3)" }}>Fax: (480) 555-0100</div>
          </div>
          <textarea className="input" rows={22} value={letter} onChange={e => setLetter(e.target.value)} style={{ fontFamily: "Georgia, serif", fontSize: 13, lineHeight: 1.85, resize: "vertical", borderRadius: "0 0 8px 8px", borderTop: "none" }} />
        </div>
      </div>

      <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, flexShrink: 0 }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setShowSend(true)} disabled={!letter.trim()}>
          📠 Send as Fax
        </button>
      </div>
    </div>,
    document.body
  );
}

// ─── CONTACTS TAB ─────────────────────────────────────────────────────────────
function ContactsTab({ onFaxNow }) {
  const [contacts, setContacts] = useState(isDemoMode ? MOCK_FAX_CONTACTS : []);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { name: "", organization: "", fax_number: "", phone: "", category: "Other", notes: "" };
  const [form, setForm] = useState(emptyForm);

  const filtered = contacts.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.organization.toLowerCase().includes(search.toLowerCase()) ||
    c.fax_number.includes(search)
  );
  const byCategory = CONTACT_CATEGORIES.reduce((acc, cat) => {
    const items = filtered.filter(c => c.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  const handleSave = () => {
    if (editId) setContacts(cs => cs.map(c => c.id === editId ? { ...c, ...form } : c));
    else setContacts(cs => [...cs, { ...form, id: "FC" + Date.now() }]);
    setShowForm(false); setEditId(null); setForm(emptyForm);
  };
  const startEdit = (c) => { setEditId(c.id); setForm({ name: c.name, organization: c.organization, fax_number: c.fax_number, phone: c.phone || "", category: c.category, notes: c.notes || "" }); setShowForm(true); };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
        <input className="input" placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
        <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(true); setEditId(null); setForm(emptyForm); }}>+ Add Contact</button>
      </div>

      {showForm && (
        <div style={{ background: "var(--navy-3)", border: "1px solid var(--border)", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 13 }}>{editId ? "Edit Contact" : "New Contact"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
            <input className="input" placeholder="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <input className="input" placeholder="Organization" value={form.organization} onChange={e => setForm(f => ({ ...f, organization: e.target.value }))} />
            <input className="input" placeholder="Fax number" value={form.fax_number} onChange={e => setForm(f => ({ ...f, fax_number: e.target.value }))} />
            <input className="input" placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {CONTACT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <input className="input" placeholder="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={!form.name || !form.fax_number}>Save Contact</button>
          </div>
        </div>
      )}

      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
            {CAT_ICONS[cat] || "📋"} {cat}
          </div>
          <div style={{ background: "var(--navy-2)", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
            {items.map((c, i) => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: i < items.length - 1 ? "1px solid var(--border)" : "none" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.organization} · Fax: {c.fax_number}{c.phone ? ` · Ph: ${c.phone}` : ""}</div>
                  {c.notes && <div style={{ fontSize: 11, color: "var(--text-3)", fontStyle: "italic" }}>{c.notes}</div>}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => onFaxNow && onFaxNow(c)}>📠 Fax</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => startEdit(c)}>Edit</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} onClick={() => setContacts(cs => cs.filter(x => x.id !== c.id))}>Del</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {Object.keys(byCategory).length === 0 && <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-3)" }}>No contacts found</div>}
    </div>
  );
}

// ─── FAX INBOX WIDGET (Dashboard) ────────────────────────────────────────────
export function FaxInboxWidget({ onNavigate }) {
  const appCtx = useApp();
  const [unrouted, setUnrouted] = useState(
    isDemoMode ? MOCK_FAX_INBOX.filter(f => f.unread) : []
  );

  useEffect(() => {
    if (isDemoMode || !appCtx?.practiceId) return;
    db
      .from('fax_transmissions')
      .select('id, from_number, pages, sent_at')
      .eq('practice_id', appCtx.practiceId)
      .eq('direction', 'inbound')
      .is('routed_at', null)
      .order('sent_at', { ascending: false })
      .limit(10)
      .then(({ data }) => setUnrouted(data || []));
  }, [appCtx?.practiceId]);

  if (unrouted.length === 0) return (
    <div className="stat-card stat-card-link" style={{ cursor: "pointer" }} onClick={() => onNavigate && onNavigate("fax")}>
      <div className="stat-label">📠 Fax Inbox</div>
      <div className="stat-value">0</div>
      <div className="stat-sub">Inbox clear</div>
    </div>
  );

  return (
    <div className="card" style={{ gridColumn: "1 / -1", border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.04)' }}>
      <div className="card-header">
        <span className="card-title">📠 Unrouted Faxes</span>
        <span className="badge badge-amber">{unrouted.length} unrouted</span>
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => onNavigate && onNavigate("fax")}>View Fax Inbox →</button>
      </div>
      {unrouted.slice(0, 3).map(f => (
        <div key={f.id} className="tr-clickable" onClick={() => onNavigate && onNavigate("fax")}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {isDemoMode ? f.subject : `Inbound Fax from ${f.from_number || 'Unknown'}`}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-3)" }}>
              {fmtTime(isDemoMode ? f.received_at : f.sent_at)} · {f.pages || '?'}pp · Needs routing
            </div>
          </div>
          <span className="badge badge-amber" style={{ fontSize: 10 }}>Unrouted</span>
        </div>
      ))}
      {unrouted.length > 3 && (
        <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--text-3)' }}>
          +{unrouted.length - 3} more unrouted fax{unrouted.length - 3 > 1 ? 'es' : ''}
        </div>
      )}
    </div>
  );
}

// ─── FAX PAGE (Standalone) ────────────────────────────────────────────────────
export default function FaxPage({ patients, onPatientSelect }) {
  const appCtx          = useApp();
  const currentProvider = appCtx?.currentProvider;

  const [tab, setTab] = useState("inbox");
  const [inbox, setInbox] = useState(isDemoMode ? MOCK_FAX_INBOX : []);
  const [sent, setSent] = useState(isDemoMode ? MOCK_SENT : []);
  const [drafts, setDrafts] = useState(isDemoMode ? MOCK_DRAFTS : []);
  const [selectedFax, setSelectedFax] = useState(null);
  const [showSend, setShowSend] = useState(false);
  const [sendPrefill, setSendPrefill] = useState(null);
  const [inboxFilter, setInboxFilter] = useState("all");
  const [sentFilter, setSentFilter] = useState("all");
  const [sentSearch, setSentSearch] = useState("");
  const [inboxPage, setInboxPage] = useState(1);
  const [sentPage, setSentPage] = useState(1);
  const [linkingFax, setLinkingFax] = useState(null);
  const debouncedSentSearch = useDebounce(sentSearch);

  // Load real inbound faxes from fax_transmissions in production
  useEffect(() => {
    if (isDemoMode || !appCtx?.practiceId) return;
    db
      .from('fax_transmissions')
      .select('*')
      .eq('practice_id', appCtx.practiceId)
      .eq('direction', 'inbound')
      .order('sent_at', { ascending: false })
      .limit(50)
      .then(({ data, error }) => {
        if (error) { console.error('[fax-inbox] load error:', error.message); return; }
        setInbox((data || []).map(normalizeInbound));
      });
  }, [appCtx?.practiceId]);

  const unreadCount = inbox.filter(f => f.unread).length;

  const handleSelectInbox = (f) => {
    setSelectedFax(f);
    if (f.unread) setInbox(prev => prev.map(x => x.id === f.id ? { ...x, unread: false } : x));
  };

  const handleAssign = async (faxId, patientId) => {
    setInbox(prev => prev.map(f => f.id === faxId ? { ...f, patient_id: patientId, patient_assigned: true } : f));
    if (!isDemoMode) {
      const { error } = await db.from('fax_transmissions').update({
        patient_id: patientId,
        routed_at:  new Date().toISOString(),
        routed_by:  currentProvider?.email || 'staff',
      }).eq('id', faxId);
      if (error) console.error('[fax-inbox] assign error:', error.message);
    }
  };

  const handleLinked = (faxId, patient) => {
    const name = `${patient.first_name || patient.first || ''} ${patient.last_name || patient.last || ''}`.trim();
    setInbox(prev => prev.map(f => f.id === faxId
      ? { ...f, patient_id: patient.id, patient_assigned: true, routed_to: name, routed_at: new Date().toISOString(), unread: false }
      : f
    ));
    if (selectedFax?.id === faxId) {
      setSelectedFax(prev => ({ ...prev, patient_id: patient.id, patient_assigned: true, routed_to: name }));
    }
    setLinkingFax(null);
  };

  async function handleViewPDF(fax) {
    if (fax.storage_path && !fax.storage_path.includes('undefined')) {
      const signedUrl = await storage.getSignedUrl('fax-documents', fax.storage_path, 3600).catch(() => '');
      if (signedUrl) { window.open(signedUrl, '_blank'); return; }
    }
    // storage_path missing or signed URL failed — should not reach here
    // since View PDF button is only shown when hasPDF is true
    console.warn('[fax-viewer] handleViewPDF: no signed URL, storage_path:', fax.storage_path);
  }

  const handleRoute  = (faxId, provider) => setInbox(prev => prev.map(f => f.id === faxId ? { ...f, routed_to: provider } : f));
  const handleReply  = (fax) => { setSendPrefill({ recipient_name: fax.sender_name, recipient_fax: fax.sender_fax, subject: `RE: ${fax.subject}` }); setShowSend(true); };
  const handleFaxNow = (contact) => { setSendPrefill({ recipient_name: contact.name, recipient_fax: contact.fax_number }); setShowSend(true); };
  const handleRetry  = (faxId) => {
    setSent(prev => prev.map(f => f.id === faxId ? { ...f, status: "queued", failed_reason: null } : f));
    setTimeout(() => setSent(prev => prev.map(f => f.id === faxId ? { ...f, status: "sent", sent_at: new Date().toISOString(), confirmation_number: genConf() } : f)), 2000);
  };

  const filteredInbox = inbox.filter(f =>
    inboxFilter === "unread" ? f.unread :
    inboxFilter === "assigned" ? f.patient_assigned :
    inboxFilter === "unassigned" ? !f.patient_assigned : true
  );
  const filteredSent = sent.filter(f =>
    (sentFilter === "all" || f.status === sentFilter) &&
    (!debouncedSentSearch || f.recipient_name.toLowerCase().includes(debouncedSentSearch.toLowerCase()) || (f.subject || "").toLowerCase().includes(debouncedSentSearch.toLowerCase()))
  );
  const pagedInbox = filteredInbox.slice((inboxPage - 1) * PAGE_SIZE, inboxPage * PAGE_SIZE);
  const pagedSent  = filteredSent.slice((sentPage - 1) * PAGE_SIZE, sentPage * PAGE_SIZE);

  const tabs = [
    { id: "inbox",    label: "Inbox",    badge: unreadCount || null },
    { id: "sent",     label: "Sent",     badge: null },
    { id: "drafts",   label: "Drafts",   badge: drafts.length || null },
    { id: "contacts", label: "Contacts", badge: null },
  ];

  return (
    <div className="slide-in" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {showSend && <FaxPanel patient={null} patients={patients} prefill={sendPrefill} onClose={() => { setShowSend(false); setSendPrefill(null); }} />}
      {linkingFax && (
        <LinkToPatientModal
          fax={linkingFax}
          patients={patients}
          currentProvider={currentProvider}
          onClose={() => setLinkingFax(null)}
          onLinked={handleLinked}
        />
      )}}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>📠 Electronic Fax</div>
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>Desert Orthopedics Center · Fax: (480) 555-0100</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setSendPrefill(null); setShowSend(true); }}>+ New Fax</button>
      </div>

      <div className="tabs" style={{ marginBottom: 0, flexShrink: 0 }}>
        {tabs.map(t => (
          <div key={t.id} className={`tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {t.label}
            {t.badge ? <span className="nav-badge" style={{ position: "static" }}>{t.badge}</span> : null}
          </div>
        ))}
      </div>

      {/* ── INBOX ── */}
      {tab === "inbox" && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
            {/* List */}
            <div style={{ width: 360, flexShrink: 0, borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {[["all","All"],["unread","Unread"],["assigned","Assigned"],["unassigned","Unassigned"]].map(([v, l]) => (
                    <button key={v} onClick={() => setInboxFilter(v)} className={`btn btn-sm ${inboxFilter === v ? "btn-secondary" : "btn-ghost"}`} style={{ fontSize: 11 }}>{l}</button>
                  ))}
                </div>
              </div>
              <div style={{ flex: 1, overflowY: "auto" }}>
                {pagedInbox.map(f => (
                  <div key={f.id} onClick={() => handleSelectInbox(f)}
                    style={{ padding: "11px 13px", borderBottom: "1px solid var(--border)", cursor: "pointer", background: selectedFax?.id === f.id ? "var(--teal-lt)" : f.unread ? "var(--navy-3)" : "transparent", borderLeft: f.unread ? "3px solid var(--teal)" : "3px solid transparent" }}
                    className="tr-clickable"
                  >
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                      {f.unread && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--teal)", flexShrink: 0, marginTop: 5 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: f.unread ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.subject}</div>
                        <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{f.sender_name}</div>
                        <div style={{ display: "flex", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 10, color: "var(--text-3)", fontFamily: "var(--mono)" }}>{fmtTime(f.received_at)}</span>
                          <span style={{ fontSize: 10, color: "var(--text-3)" }}>{f.pages}pp</span>
                          {f.patient_assigned && <span className="badge badge-green" style={{ fontSize: 9, padding: "0 4px" }}>Assigned</span>}
                          {f.routed_to && <span className="badge badge-teal" style={{ fontSize: 9, padding: "0 4px" }}>Routed</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredInbox.length === 0 && <div style={{ padding: "32px 20px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>No faxes in this view</div>}
                <div style={{ padding: "0 12px" }}>
                  <Pagination currentPage={inboxPage} totalPages={Math.ceil(filteredInbox.length / PAGE_SIZE)} onPageChange={p => setInboxPage(p)} totalItems={filteredInbox.length} pageSize={PAGE_SIZE} />
                </div>
              </div>
            </div>
            {/* Viewer */}
            <FaxViewer fax={selectedFax} onAssign={handleAssign} onRoute={handleRoute} onReply={handleReply} patients={patients}
              onViewPDF={handleViewPDF}
              onLinkPatient={isDemoMode ? null : f => setLinkingFax(f)} />
          </div>
          {/* Audit log */}
          <div style={{ borderTop: "1px solid var(--border)", padding: "12px 16px", maxHeight: 260, overflowY: "auto", flexShrink: 0, background: "var(--navy)" }}>
            <AuditLog entries={AUDIT_INBOX} />
          </div>
        </div>
      )}

      {/* ── SENT ── */}
      {tab === "sent" && (
        <div style={{ flex: 1, overflowY: "auto", paddingTop: 16 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
            <input className="input" placeholder="Search sent faxes..." value={sentSearch} onChange={e => setSentSearch(e.target.value)} style={{ flex: 1, minWidth: 180 }} />
            <div style={{ display: "flex", gap: 5 }}>
              {[["all","All"],["queued","Queued"],["sent","Sent"],["confirmed","Confirmed"],["failed","Failed"]].map(([v, l]) => (
                <button key={v} onClick={() => setSentFilter(v)} className={`btn btn-sm ${sentFilter === v ? "btn-secondary" : "btn-ghost"}`} style={{ fontSize: 11 }}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{ background: "var(--navy-2)", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", marginBottom: 4 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--navy-3)" }}>
                  {["Recipient","Patient","Subject","Sent","Pages","Status",""].map(h => (
                    <th key={h} style={{ padding: "9px 13px", textAlign: "left", fontSize: 11, color: "var(--text-3)", fontWeight: 500, borderBottom: "1px solid var(--border)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedSent.map(f => (
                  <tr key={f.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "10px 13px" }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{f.recipient_name}</div>
                      <div style={{ fontSize: 11, color: "var(--text-3)" }}>{f.recipient_fax}</div>
                    </td>
                    <td style={{ padding: "10px 13px", fontSize: 12, color: "var(--text-2)" }}>{f.patient_name || "—"}</td>
                    <td style={{ padding: "10px 13px", fontSize: 12, maxWidth: 200 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.subject}</div>
                      {f.is_referral && <span className="badge badge-teal" style={{ fontSize: 9, marginTop: 2 }}>Referral</span>}
                    </td>
                    <td style={{ padding: "10px 13px", fontSize: 11, color: "var(--text-3)", fontFamily: "var(--mono)", whiteSpace: "nowrap" }}>{fmtTime(f.sent_at)}</td>
                    <td style={{ padding: "10px 13px", fontSize: 12, color: "var(--text-2)", textAlign: "center" }}>{f.pages}</td>
                    <td style={{ padding: "10px 13px" }}>
                      <StatusBadge status={f.status} />
                      {f.status === "confirmed" && f.confirmation_number && <div style={{ fontSize: 10, color: "var(--green)", fontFamily: "var(--mono)", marginTop: 2 }}>✓ {f.confirmation_number}</div>}
                      {f.status === "failed" && f.failed_reason && <div style={{ fontSize: 10, color: "var(--red)", marginTop: 2 }}>{f.failed_reason}</div>}
                    </td>
                    <td style={{ padding: "10px 13px" }}>
                      {f.status === "failed" && <button className="btn btn-sm btn-secondary" style={{ fontSize: 11 }} onClick={() => handleRetry(f.id)}>Retry</button>}
                    </td>
                  </tr>
                ))}
                {filteredSent.length === 0 && <tr><td colSpan={7} style={{ padding: 32, textAlign: "center", color: "var(--text-3)" }}>No faxes found</td></tr>}
              </tbody>
            </table>
          </div>
          <Pagination currentPage={sentPage} totalPages={Math.ceil(filteredSent.length / PAGE_SIZE)} onPageChange={p => setSentPage(p)} totalItems={filteredSent.length} pageSize={PAGE_SIZE} />
          <AuditLog entries={AUDIT_SENT} />
        </div>
      )}

      {/* ── DRAFTS ── */}
      {tab === "drafts" && (
        <div style={{ flex: 1, overflowY: "auto", paddingTop: 16 }}>
          {drafts.length === 0 && <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-3)" }}>No saved drafts</div>}
          {drafts.map(d => (
            <div key={d.id} style={{ background: "var(--navy-2)", border: "1px solid var(--border)", borderRadius: 8, padding: 16, marginBottom: 10, display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{d.subject || "(No subject)"}</div>
                <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                  To: {d.recipient_name || "(no recipient)"}{d.recipient_fax ? ` · ${d.recipient_fax}` : ""} · {d.patient_name || "No patient"}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3, fontFamily: "var(--mono)" }}>Saved {fmtTime(d.created_at)} · {d.pages}pp</div>
                {d.fax_body && <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.fax_body}</div>}
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button className="btn btn-primary btn-sm" onClick={() => { setSendPrefill({ recipient_name: d.recipient_name, recipient_fax: d.recipient_fax, subject: d.subject, fax_body: d.fax_body }); setShowSend(true); }}>Resume</button>
                <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} onClick={() => setDrafts(prev => prev.filter(x => x.id !== d.id))}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── CONTACTS ── */}
      {tab === "contacts" && (
        <div style={{ flex: 1, overflowY: "auto", paddingTop: 16 }}>
          <ContactsTab onFaxNow={handleFaxNow} />
        </div>
      )}
    </div>
  );
}
